import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import moment from 'moment';
import { Observable, Subject, Subscription } from 'rxjs';
import { Watchable } from '../../objects/configAttributes/configAttributeInterfaces';
import { BoundingBox } from './boundingBox';
import { LegendControl } from './controls/legendControl/legendControl';
import { LayerClickManager } from './layerClickManager/layerClickManager';
import { MapLayer } from './layers/mapLayer.abstract';
import { MoveMethod } from './moveMethod.enum';

@Component({
  selector: 'bgs-leaflet-map',
  templateUrl: 'bgsLeaflet.component.html',
  styleUrls: [
    'bgsLeaflet.component.scss',
    './marker/faMarker/faMarker.scss',
    './featureDisplay/paginatedFeatures.scss',
    './controls/abstractControl/abstractControl.scss',
    './controls/baseLayerControl/baseLayerControl.scss',
    './controls/drawBBoxControl/drawBBoxControl.scss',
    './controls/layerControl/layerControl.scss',
    './controls/legendControl/legendControl.scss',
    './controls/searchControl/searchControl.scss',
  ],
})
export class BgsLeafletComponent implements AfterViewInit {
  @Input() initialLatLng: [number, number] = [52.8, -1.0];
  @Input() initialZoom = 4;
  @Input() minZoom = 2;
  @Input() maxZoom = 12;
  @Input() crs = L.CRS.EPSG3857;
  @Input() zoomable = true;
  @Input() dragable = true;

  @Output() onload = new EventEmitter<BgsLeafletComponent>();

  @ViewChild('mapElement', { static: true }) protected mapElement: ElementRef<HTMLElement>;

  public leafletMapObj: L.Map;
  public showLoader = false;

  protected LOADER_DELAY_MS = 200; // delay before loader is shown.

  protected movingMap = false;

  // protected pesistTimer: any;
  // public drawLayer: boolean | any = false;

  protected layers = new Array<MapLayer>();
  protected layersChangeTimeout: NodeJS.Timeout;
  protected layersChangeSource = new Subject<Array<MapLayer>>();
  protected loadingLayerCount = 0;
  protected loaderCheckInterval: NodeJS.Timeout;

  protected layerClickManager: null | LayerClickManager;

  protected legendControl: LegendControl;

  protected layerRedrawQueues = new Map<string, Array<MapLayer | string>>();
  protected redrawQueueProcessing = new Map<string, boolean>();

  protected layerOrderChangeQueue = new Array<[string, boolean]>();

  protected layersOrderTimer: NodeJS.Timeout;

  protected layerPositionMonitorFront = new Map<string, Subscription>();
  protected layerPositionMonitorBack = new Map<string, Subscription>();
  protected layerSetNotHiddenMonitor = new Map<string, Subscription>();

  constructor(protected elRef: ElementRef<HTMLElement>, protected http: HttpClient) {}

  /* after html generated */
  public ngAfterViewInit(): void {
    this.leafletMapObj = L.map(this.getMapElement(), {
      worldCopyJump: true,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      zoomControl: false,
      // attributionControl: false,
      crs: this.crs,
    } as L.MapOptions);

    this.leafletMapObj.on('load', () => this.onload.emit(this));

    this.leafletMapObj.setView(L.latLng(this.initialLatLng), this.initialZoom);

    if (!this.zoomable) {
      this.leafletMapObj.removeControl(L.control.zoom());
      this.leafletMapObj.touchZoom.disable();
      this.leafletMapObj.doubleClickZoom.disable();
      this.leafletMapObj.scrollWheelZoom.disable();
      this.leafletMapObj.boxZoom.disable();
      this.leafletMapObj.keyboard.disable();
    }

    if (!this.dragable) {
      this.leafletMapObj.dragging.disable();
    }

    // init event listeners
    this.leafletMapObj.on('zoomend', () => {
      this.postLayerManipulationNormalization(false);
    });

    this.leafletMapObj.on('zoomstart', () => {
      this.leafletMapObj.closePopup();
      // console.debug(L.popup());
    });

    this.leafletMapObj.on('moveend ', () => {
      this.postLayerManipulationNormalization(false);
      if (!this.movingMap) {
        this.movingMap = true;

        // ensure map is not way up or down
        const bounds = this.leafletMapObj.getBounds();
        if (bounds.getNorth() > 89) {
          this.leafletMapObj.fitBounds([
            [bounds.getSouth(), bounds.getWest()],
            [80, bounds.getEast()],
          ]);
        } else if (bounds.getSouth() < -89) {
          this.leafletMapObj.fitBounds([
            [-80, bounds.getWest()],
            [bounds.getNorth(), bounds.getEast()],
          ]);
        }
        this.movingMap = false;
      }
      // this.updateMapExtent();
    });

    this.leafletMapObj.on('click', (clickEvent: L.LeafletMouseEvent) => {
      // console.log('mapClick');
      if (null != this.layerClickManager) {
        this.layerClickManager.click(clickEvent, this);
      }
    });

    // this.setBbox(mapState.bbox);

    // run async so that it happens after angular and map is ready
    // setTimeout(() => {
    //   this.addLayers(mapState.layers);

    //   // adds controls in the correct order.
    //   // This needs to be in this delay block because esri baselayers error on referencing the
    //   // attribution control (map credits in bottom right corner)
    //   this.mapSettings.controls.forEach((control: L.Control) => {
    //     if (typeof control['setConfig'] === 'function') {
    //       control['setConfig'](this.config);
    //     }
    //     control.addTo(this.leafletMapObj);
    //   });
    // }, 200);

    this.leafletMapObj.invalidateSize();
  }

  public getElement(): HTMLElement {
    return this.elRef.nativeElement;
  }

  public watchLayers(): Observable<Array<MapLayer>> {
    return this.layersChangeSource.asObservable();
  }

  public getLayers(): Array<MapLayer> {
    return this.layers.slice();
  }

  public addControl(control: L.Control): this {
     
    if (typeof control['setMapComponent'] === 'function') {
       
      control['setMapComponent'](this);
    }
    if (control instanceof LegendControl) {
      this.legendControl = control;
      this.legendControl.setHttpClient(this.http);
    }
    control.addTo(this.leafletMapObj);
    return this;
  }

  public getMapExtent(): BoundingBox {
    const bounds = this.leafletMapObj.getBounds();
    const mapExtent = new BoundingBox(
      Math.min(bounds.getNorth(), 90),
      bounds.getEast(),
      Math.max(bounds.getSouth(), -90),
      bounds.getWest(),
    );
    return mapExtent;
  }

  // /**
  //  * sends the current map state to the adapter service
  //  */
  // protected reallyPersistMapState() {
  //   try {
  //     // Bbox of drawing laye
  //     const bbox = this.getBoundingBox();

  //     // Bbox of visible map
  //     const mapBounds = this.getMapExtent();

  //     const mapState = new MapState(
  //       [this.leafletMapObj.getCenter().lat, this.leafletMapObj.getCenter().lng],
  //       this.leafletMapObj.getZoom(),
  //       this.layers.slice(),
  //       mapBounds,
  //       bbox,
  //       [this.leafletMapObj.getSize().x, this.leafletMapObj.getSize().y]
  //     );

  //     this.config._setMapState(mapState);
  //   } catch (err) {
  //     console.warn('Map not ready to persist state.');
  //   }
  // }

  // protected persistMapState() {
  //   const that = this;
  //   clearTimeout(this.pesistTimer);
  //   this.pesistTimer = setTimeout(() => {
  //     // inactive for delay
  //     that.reallyPersistMapState();
  //   }, 100);
  // }

  public getLeafletObject(): L.Map {
    return this.leafletMapObj;
  }

  public getMapElement(): HTMLElement {
    return this.mapElement.nativeElement;
  }

  // protected getLayerById(layerId: string): MapLayer {
  //   let returnLayer = null;
  //   this.layers.slice().forEach((layer: MapLayer) => {
  //     if (layerId === layer.id) {
  //       returnLayer = layer;
  //     }
  //   });
  //   return returnLayer;
  // }

  // ****START LAYER MANIPULATION */

  public addLayers(layers: Array<MapLayer>): void {
    layers.forEach((layer: MapLayer) => {
      this.addLayer(layer);
    });
  }

  public addLayer(layer: MapLayer): this {
    // console.debug('addLayer ');
    this.getRedrawQueue(layer.id).push(layer);
    this.tryStartRedrawProcessing(layer.id);
    return this;
  }

  public removeLayerById(layerId: string): this {
    this.getRedrawQueue(layerId).push(layerId);
    this.tryStartRedrawProcessing(layerId);
    return this;
  }

  // redraw (or just add or remove) a layer
  public redrawLayer(layer: MapLayer | string): Promise<void> {
    const layerId = typeof layer === 'string' ? layer : layer.id;
    const layerToAdd = typeof layer === 'string' ? null : layer;

    this.triggerLayerLoader();
    // in case that layer has changed (same id) get a ref of current one
    const layerToRemove = this.layers.find((thisLayer: MapLayer) => thisLayer.id === layerId);
    return (
      layerToRemove == null
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            this.removeLayerMonitor(layerToRemove.bringToFront, this.layerPositionMonitorFront);
            this.removeLayerMonitor(layerToRemove.bringToBack, this.layerPositionMonitorBack);
            this.removeLayerMonitor(layerToRemove.hidden, this.layerSetNotHiddenMonitor);
            resolve(layerToRemove.removeSelfFromMap());
          })
    ).then(() => {
      // update layers
      const currentIndex = this.layers.findIndex((thisLayer: MapLayer) => thisLayer.id === layerId);
      // adding
      if (null != layerToAdd) {
        // new
        if (currentIndex === -1) {
          this.layers.push(layerToAdd);
        } else {
          // If replace, put back in the same place
          // Check if style params set on new layer and if not, copy across from old.
          if (layerToAdd.options.opacity.get() == null) {
             
            layerToAdd.options.opacity.set(layerToRemove!.options.opacity.get());
          }
          this.layers.splice(currentIndex, 1, layerToAdd);
        }
      } else if (currentIndex > -1) {
        // just remove
        this.layers.splice(currentIndex, 1);
      }
      this.broadcastLayerChange();

      return (
        null == layerToAdd
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              // add layer monitors
              this.addLayerMonitor(layerToAdd.bringToBack, this.layerPositionMonitorBack, () => {
                this.requestLayerOrderChange(layerId, false);
              });
              this.addLayerMonitor(layerToAdd.bringToFront, this.layerPositionMonitorFront, () => {
                this.requestLayerOrderChange(layerId, true);
              });
              this.addLayerMonitor(layerToAdd.hidden, this.layerSetNotHiddenMonitor, () => {
                if (layerToAdd.hidden.get()) {
                  // will need to remove the corresponding legend
                  this.updateLegend();
                } else {
                  // results in layer order and legend refresh if required.
                  this.ensureLayersOrdered();
                }
              });
              resolve(layerToAdd.addSelfToMap(this));
            })
      ).then(() => {
        this.triggerLayerLoader(true);
      });
    });
  }

  public fitBounds(bounds: L.LatLngBoundsExpression, options?: L.FitBoundsOptions): this {
    this.leafletMapObj.fitBounds(bounds, options);
    return this;
  }

  public moveView(
    lat: number,
    lon: number,
    method = MoveMethod.PAN,
    zoom = 10,
    latOffset = 0.0, // used to offset map so that popup is visible on point view
    lonOffset = 0.0, // used to offset map so that popup is visible on point view
  ): this {
    this.reallyMoveView(lat, lon, method, zoom, latOffset, lonOffset);
    // do it again to make sure it happened
    setTimeout(() => {
      this.reallyMoveView(lat, lon, method, zoom, latOffset, lonOffset);
    }, 400);
    return this;
  }

  public resized(): void {
    if (this.leafletMapObj) {
      setTimeout(() => {
        this.leafletMapObj.invalidateSize(false);
      }, 0);
    }
  }

  public closePopup(): void {
    if (this.leafletMapObj) {
      this.leafletMapObj.closePopup();
    }
  }

  public getMapSize(): { widthPx: number; heightPx: number } {
    return {
      widthPx: this.leafletMapObj.getSize().x,
      heightPx: this.leafletMapObj.getSize().y,
    };
  }

  public enableLayerClickManager(manager?: LayerClickManager): this {
    manager = null == manager ? new LayerClickManager() : manager;
    manager.init(this.leafletMapObj, this.http);
    this.layerClickManager = manager;
    return this;
  }
  public disableLayerClickManager(): this {
    this.layerClickManager = null;
    return this;
  }
  public getLayerClickManager(): null | LayerClickManager {
    return this.layerClickManager;
  }

  // add layer then test the leaflet map object layers
  // until it appears, to ensure not added more than once.
  // protected ensureLeafletLayerAdded(layer: MapLayer): Promise<void> {
  //   // console.debug('ensureLeafletLayerAdded', layer.id);
  //   this.triggerLayerLoader();
  //   return layer
  //     .addSelfToMap(this.leafletMapObj)
  //     .then(() => {
  //       this.triggerLayerLoader(true);
  //     });
  // }

  protected broadcastLayerChange(): void {
    clearTimeout(this.layersChangeTimeout);
    this.layersChangeTimeout = setTimeout(() => {
      this.layersChangeSource.next(this.layers.slice());
    }, 300);
  }

  protected getRedrawQueue(layerId: string): Array<MapLayer | string> {
    if (!this.layerRedrawQueues.has(layerId)) {
      this.layerRedrawQueues.set(layerId, []);
    }
    return this.layerRedrawQueues.get(layerId)!;
  }

  // recursive function for synchronously processing layerId redraw queues
  protected redrawNextLayer(layerId: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const queue = this.getRedrawQueue(layerId);
      let redrawItem: undefined | string | MapLayer;
      while (queue.length > 0) {
        redrawItem = queue.shift();
      }
      if (null != redrawItem) {
        // console.debug('redrawNextLayer', redrawItem);
        resolve(this.redrawLayer(redrawItem).then(() => this.redrawNextLayer(layerId)));
      } else {
        // all done
        // console.debug('redrawNextLayer finished', this.layers.slice());

        resolve();
      }
    });
  }

  protected tryStartRedrawProcessing(layerId: string): void {
    // only kick it off if it's not already running
    if (!this.redrawQueueProcessing.get(layerId)) {
      this.redrawQueueProcessing.set(layerId, true);
      // console.log('map starting redraw');
      void this.redrawNextLayer(layerId).then(() => {
        this.redrawQueueProcessing.delete(layerId);
        this.postLayerManipulationNormalization(true);
        // console.log('map finished redraw');
      });
    }
  }

  protected postLayerManipulationNormalization(layersRedrawn: boolean): void {
    if (Array.from(this.redrawQueueProcessing.values()).length === 0) {
      this.processLayerOrderChangeQueue();

      if (layersRedrawn) {
        this.ensureLayersOrdered();
      }

      this.updateLegend();
    }
  }

  protected removeLayerMonitor(
    watchable: Watchable<MapLayer, unknown>,
    subscriptionMap: Map<string, Subscription>,
  ): void {
    const layerId = watchable.context().id;
    const currentSubscription = subscriptionMap.get(layerId);
    if (currentSubscription != null) {
      currentSubscription.unsubscribe();
    }
    subscriptionMap.delete(layerId);
  }
  protected addLayerMonitor(
    watchable: Watchable<MapLayer, unknown>,
    subscriptionMap: Map<string, Subscription>,
    observerFunction: () => void,
  ): void {
    const layerId = watchable.context().id;
    this.removeLayerMonitor(watchable, subscriptionMap);
    subscriptionMap.set(layerId, watchable.watch().subscribe(observerFunction));
  }

  // loader is delayed from showing straight away
  protected triggerLayerLoader(end = false): void {
    const date = moment();
    if (end) {
      this.loadingLayerCount--;
      if (this.loadingLayerCount < 1) {
        this.loadingLayerCount = 0;
        clearTimeout(this.loaderCheckInterval);
        this.showLoader = false;
      }
    } else {
      this.loadingLayerCount++;
      if (this.loadingLayerCount === 1) {
        this.loaderCheckInterval = setInterval(() => {
          if (
            !this.showLoader &&
            this.loadingLayerCount > 0 &&
            moment(date).add(this.LOADER_DELAY_MS, 'ms') < moment()
          ) {
            clearTimeout(this.loaderCheckInterval);
            this.showLoader = true;
          }
        }, this.LOADER_DELAY_MS / 2);
      }
    }
  }

  // protected createPane(paneId: string, zIndex?: number): HTMLElement {
  //   let pane = null;
  //   if (paneId != null) {
  //     pane = this.leafletMapObj.getPane(paneId);
  //     if (pane == null) {
  //       pane = this.leafletMapObj.createPane(paneId);
  //       pane.style.zIndex = zIndex || 600;
  //     }
  //   }
  //   return pane;
  // }

  // protected getPane(paneId: string): HTMLElement {
  //   // let pane = this.leafletMapObj.getPane(paneId);
  //   // if (pane == null) {
  //   //   pane = this.createPane(paneId);
  //   // }
  //   return this.leafletMapObj.getPane(paneId);
  // }
  // protected removePane(paneId: string): any {
  //   const pane = this.getPane(paneId);
  //   if (pane != null) {
  //     L.DomUtil.remove(pane);
  //   }
  // }

  // ****END LAYER MANIPULATION */

  // seems to need this to be called after zoom and pan to ensure layers (just esri dynamic?)
  // are still properly ordered.
  protected ensureLayersOrdered(): void {
    // console.debug(`ensureLayersOrdered`, this.layers.slice());
    // slice or it will change after we start the loop!
    this.layers.slice().forEach((layer: MapLayer) => {
      layer.bringToFront.set();
    });
  }

  protected processLayerOrderChangeQueue(): void {
    const changeQueue = this.layerOrderChangeQueue.slice();
    this.layerOrderChangeQueue = [];
    changeQueue.forEach((changeOrderRequest: [string, boolean]) => {
      const id = changeOrderRequest[0];
      const toFront = changeOrderRequest[1];
      // console.debug('changeLayerOrder', id, toFront);
      const layerIndex = this.layers.findIndex((thisLayer: MapLayer) => thisLayer.id === id);

      if (layerIndex === -1) {
        console.log(`Can't re-order layer ${id} - not found.`);
      } else {
        const mapLayer = this.layers.splice(layerIndex, 1)[0];
        if (toFront) {
          this.layers.push(mapLayer);
        } else {
          this.layers.unshift(mapLayer);
        }
      }
    });
    this.broadcastLayerChange();
  }

  protected requestLayerOrderChange(id: string, toFront: boolean): void {
    this.layerOrderChangeQueue.push([id, toFront]);

    // let all the calls be done before processing.
    clearTimeout(this.layersOrderTimer);
    this.layersOrderTimer = setTimeout(() => {
      this.postLayerManipulationNormalization(false);
    }, 300);
  }

  // protected isBboxSame(testBbox: BoundingBox): boolean {
  //   const currentBbox = this.getBbox();
  //   if (currentBbox === testBbox) {
  //     return true;
  //   }
  //   if (
  //     ((currentBbox == null) && (testBbox !== null))
  //     || ((testBbox == null) && (currentBbox !== null))
  //   ) {
  //     return false;
  //   }
  //   for (let i = 0; i < currentBbox.length; i++) {
  //     if (currentBbox[i] !== testBbox[i]) {
  //       return false;
  //     }
  //   }
  //   return true;
  // }

  // public fitBounds(
  //   south: number,
  //   west: number,
  //   north: number,
  //   east: number,
  //   paddingLat = 0.0,
  //   paddingLon = 0.0,
  // ): this {
  //   const bounds = L.latLngBounds([
  //     [north, east],
  //     [south, west],
  //   ]);
  //   const padding = {
  //     padding: [paddingLat, paddingLon]
  //   } as L.FitBoundsOptions;
  //   this.leafletMapObj.fitBounds(bounds, padding);
  //   return this;
  // }
  protected reallyMoveView(
    lat: number,
    lon: number,
    method: MoveMethod,
    zoom = 10,
    latOffset: number, // used to offset map so that popup is visible on point view
    lonOffset: number, // used to offset map so that popup is visible on point view
  ): void {
    this.leafletMapObj.closePopup();
    // this.leafletMapObj.setZoom(1); // without this it seems to get lost.

    // offset so that popup is visible
    const targetLoc = new L.LatLng(lat - latOffset, lon + lonOffset);

    const options = {
      animate: true,
    };
    if (method === MoveMethod.ZOOM) {
      this.leafletMapObj.setView(targetLoc, zoom, options);
    } else if (method === MoveMethod.FLY) {
      this.leafletMapObj.flyTo(targetLoc, zoom, options);
    } else {
      this.leafletMapObj.panTo(targetLoc, options);
    }
  }

  protected updateLegend(): void {
    if (this.legendControl != null) {
      this.legendControl.updateLayers(this.getLayers());
    }
  }
}
